import { useEffect, useRef } from 'react';
import { io, type Socket } from 'socket.io-client';
import {
  ROOM_SUBSCRIBE,
  ROOM_UNSUBSCRIBE,
  RealtimeEvent,
  type TicketMessagePayload,
  type TicketUpdatedPayload,
} from '@nbh/shared';

/**
 * One shared socket for the whole app. Opening a connection per component would
 * multiply handshakes on a phone that is already short of bandwidth.
 */
let shared: Socket | null = null;

/**
 * The one shared socket. Exported so `useDirectMessages` listens on the same
 * connection rather than opening a second handshake per feature — a phone short of
 * bandwidth should carry one.
 */
export function getSocket(): Socket {
  shared ??= io({
    path: '/socket.io',
    // The session cookie authenticates the handshake; nothing is sent explicitly.
    withCredentials: true,
    // Poll first: hotel wifi and captive portals often break a bare websocket upgrade.
    transports: ['polling', 'websocket'],
    reconnectionDelay: 1000,
    reconnectionDelayMax: 10_000,
  });
  return shared;
}

/**
 * Nudges the socket after the connection returns.
 *
 * socket.io reconnects on its own, but on its backoff schedule — this brings the live
 * feed back immediately instead of up to ten seconds later. No-op if never connected,
 * so it cannot open a socket for a signed-out user.
 */
export function reconnectRealtime(): void {
  if (shared && !shared.connected) shared.connect();
}

type Handlers = {
  onTicketCreated?: (payload: TicketUpdatedPayload) => void;
  onTicketUpdated?: (payload: TicketUpdatedPayload) => void;
  onMessage?: (payload: TicketMessagePayload) => void;
};

/**
 * Subscribes to the live feed, and to one ticket thread when `ticketId` is given.
 *
 * Handlers are kept in a ref so a parent re-render does not tear down and rebuild
 * the listeners, which would drop events in the gap.
 */
export function useRealtime(handlers: Handlers, ticketId?: string): void {
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    const socket = getSocket();

    const onCreated = (payload: TicketUpdatedPayload) =>
      handlersRef.current.onTicketCreated?.(payload);
    const onUpdated = (payload: TicketUpdatedPayload) =>
      handlersRef.current.onTicketUpdated?.(payload);
    const onMessage = (payload: TicketMessagePayload) => handlersRef.current.onMessage?.(payload);

    socket.on(RealtimeEvent.TicketCreated, onCreated);
    socket.on(RealtimeEvent.TicketUpdated, onUpdated);
    socket.on(RealtimeEvent.TicketMessage, onMessage);

    return () => {
      socket.off(RealtimeEvent.TicketCreated, onCreated);
      socket.off(RealtimeEvent.TicketUpdated, onUpdated);
      socket.off(RealtimeEvent.TicketMessage, onMessage);
    };
  }, []);

  useEffect(() => {
    if (!ticketId) return;
    const socket = getSocket();

    // Re-join after a reconnect, or the thread goes quiet with no visible failure.
    const join = () => socket.emit(ROOM_SUBSCRIBE, { ticketId });
    join();
    socket.on('connect', join);

    return () => {
      socket.off('connect', join);
      socket.emit(ROOM_UNSUBSCRIBE, { ticketId });
    };
  }, [ticketId]);
}
